import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, X, ArrowLeft, Send, Check, XCircle, Upload, Loader2, Search } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Navbar } from "@/components/Navbar";
import { RichTextEditor } from "@/components/RichTextEditor";
import { ImageInputUploader } from "@/components/ImageInputUploader";

const CATEGORIES = ["Crítica", "Reseña", "Noticia", "Entrevista", "Ensayo", "Lista"];
const EMPTY = {
  title: "", category: "Crítica", excerpt: "", cover_url: "", video_url: "", content: "",
  title_en: "", excerpt_en: "", content_en: "",
  title_fr: "", excerpt_fr: "", content_fr: "",
  related_movie_ids: [], related_links: []
};

const STATUS_LABEL = { draft: "Borrador", pending: "En revisión", published: "Publicado", rejected: "Rechazado" };
const STATUS_COLOR = { draft: "text-zinc-400", pending: "text-amber-400", published: "text-emerald-400", rejected: "text-[#E11D48]" };

export default function ArticleEditor() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [articles, setArticles] = useState([]);
  const [statusFilter, setStatusFilter] = useState("");
  const [editing, setEditing] = useState(null);
  const [langTab, setLangTab] = useState("es");
  const [relatedMovies, setRelatedMovies] = useState([]);
  const [movieQuery, setMovieQuery] = useState("");
  const [movieResults, setMovieResults] = useState([]);
  const [uploadingCover, setUploadingCover] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const url = isAdmin ? "/admin/articles" : "/me/articles";
      const { data } = await api.get(url, { params: isAdmin && statusFilter ? { status: statusFilter } : {} });
      setArticles(data.articles);
    } catch {
      setArticles([]);
    }
  }, [isAdmin, statusFilter]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!movieQuery.trim()) { setMovieResults([]); return; }
    const t = setTimeout(async () => {
      const { data } = await api.get("/movies", { params: { search: movieQuery } });
      const list = Array.isArray(data) ? data : (data?.movies || []);
      setMovieResults(list.slice(0, 8));
    }, 300);
    return () => clearTimeout(t);
  }, [movieQuery]);

  const openNew = () => { setEditing({ ...EMPTY }); setRelatedMovies([]); setLangTab("es"); };

  const openEdit = async (id) => {
    const { data } = await api.get(`/articles/mine/${id}`);
    setEditing({
      id: data.id, title: data.title, category: data.category, excerpt: data.excerpt || "",
      cover_url: data.cover_url || "", video_url: data.video_url || "", content: data.content || "",
      title_en: data.title_en || "", excerpt_en: data.excerpt_en || "", content_en: data.content_en || "",
      title_fr: data.title_fr || "", excerpt_fr: data.excerpt_fr || "", content_fr: data.content_fr || "",
      related_movie_ids: data.related_movie_ids || [], related_links: data.related_links || [],
      status: data.status,
    });
    setRelatedMovies(data.related_movies || []);
    setLangTab("es");
  };

  const uploadCover = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingCover(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/uploads/image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setEditing((p) => ({ ...p, cover_url: data.url }));
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setUploadingCover(false);
      e.target.value = "";
    }
  };

  const addMovie = (m) => {
    if (editing.related_movie_ids.includes(m.id)) return;
    setEditing((p) => ({ ...p, related_movie_ids: [...p.related_movie_ids, m.id] }));
    setRelatedMovies((p) => [...p, m]);
    setMovieQuery("");
    setMovieResults([]);
  };
  const removeMovie = (id) => {
    setEditing((p) => ({ ...p, related_movie_ids: p.related_movie_ids.filter((x) => x !== id) }));
    setRelatedMovies((p) => p.filter((m) => m.id !== id));
  };

  const setLink = (i, field, val) => {
    const links = [...editing.related_links];
    links[i] = { ...links[i], [field]: val };
    setEditing({ ...editing, related_links: links });
  };
  const addLink = () => setEditing({ ...editing, related_links: [...editing.related_links, { label: "", url: "" }] });
  const removeLink = (i) => setEditing({ ...editing, related_links: editing.related_links.filter((_, x) => x !== i) });

  const save = async (submitAfter) => {
    if (!editing.title.trim()) { toast.error("El título en español es obligatorio"); return; }
    setSaving(true);
    try {
      const payload = {
        title: editing.title, category: editing.category, excerpt: editing.excerpt,
        cover_url: editing.cover_url, video_url: editing.video_url, content: editing.content,
        title_en: editing.title_en, excerpt_en: editing.excerpt_en, content_en: editing.content_en,
        title_fr: editing.title_fr, excerpt_fr: editing.excerpt_fr, content_fr: editing.content_fr,
        related_movie_ids: editing.related_movie_ids,
        related_links: editing.related_links.filter((l) => l.url),
      };
      let id = editing.id;
      if (id) await api.put(`/articles/${id}`, payload);
      else { const { data } = await api.post("/articles", payload); id = data.id; }
      if (submitAfter) await api.post(`/articles/${id}/submit`);
      toast.success(submitAfter ? "Artículo enviado a revisión" : "Artículo guardado");
      setEditing(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const doAction = async (id, action) => {
    try {
      if (action === "delete") {
        if (!window.confirm("¿Eliminar este artículo?")) return;
        await api.delete(`/articles/${id}`);
      } else {
        await api.post(`/admin/articles/${id}/${action}`);
      }
      toast.success("Hecho");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  if (editing) {
    return (
      <div className="min-h-screen bg-[#050505]" data-testid="article-editor-form">
        <Navbar />
        <div className="pt-24 px-6 md:px-16 pb-20 max-w-4xl mx-auto">
          <button onClick={() => setEditing(null)} className="flex items-center gap-2 text-zinc-400 hover:text-white text-sm mb-6" data-testid="editor-back">
            <ArrowLeft size={16} /> Volver a mis artículos
          </button>
          <h1 className="font-heading text-3xl mb-8">{editing.id ? "Editar artículo" : "Nuevo artículo"}</h1>

          {/* Language selector tabs for multi-language support */}
          <div className="flex items-center gap-2 mb-6 border-b border-white/10 pb-3">
            <span className="text-xs uppercase text-zinc-400 font-semibold mr-2">Idioma / Language:</span>
            {[
              { code: "es", name: "Español (Principal)" },
              { code: "en", name: "English" },
              { code: "fr", name: "Français" }
            ].map(t => (
              <button
                key={t.code}
                type="button"
                onClick={() => setLangTab(t.code)}
                className={`px-3 py-1.5 text-xs font-semibold rounded-md transition-all ${
                  langTab === t.code
                    ? "bg-[#E11D48] text-white shadow-md"
                    : "bg-zinc-900 text-zinc-400 hover:text-white border border-white/10"
                }`}
              >
                {t.name}
              </button>
            ))}
          </div>

          <div className="space-y-6">
            {langTab === "es" && (
              <>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="md:col-span-2">
                    <label className="text-xs tracking-wider uppercase text-zinc-400">Título (Español) *</label>
                    <input data-testid="article-title-input" value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} className="w-full bg-transparent border-b border-white/20 py-2 mt-1 outline-none focus:border-[#E11D48]" />
                  </div>
                  <div>
                    <label className="text-xs tracking-wider uppercase text-zinc-400">Categoría</label>
                    <select data-testid="article-category-select" value={editing.category} onChange={(e) => setEditing({ ...editing, category: e.target.value })} className="w-full bg-[#0A0A0C] border border-white/15 px-3 py-2 mt-1 outline-none focus:border-[#E11D48] text-sm">
                      {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </div>
                </div>

                <div>
                  <label className="text-xs tracking-wider uppercase text-zinc-400">Resumen (extracto en Español)</label>
                  <textarea data-testid="article-excerpt-input" value={editing.excerpt} onChange={(e) => setEditing({ ...editing, excerpt: e.target.value })} rows={2} className="w-full bg-transparent border border-white/15 p-3 mt-1 outline-none focus:border-[#E11D48] resize-none text-sm" />
                </div>

                <div>
                  <label className="text-xs tracking-wider uppercase text-zinc-400 block mb-2">Contenido (Español)</label>
                  <RichTextEditor value={editing.content} onChange={(v) => setEditing({ ...editing, content: v })} />
                </div>
              </>
            )}

            {langTab === "en" && (
              <>
                <div>
                  <label className="text-xs tracking-wider uppercase text-zinc-400">Title (English)</label>
                  <input value={editing.title_en || ""} onChange={(e) => setEditing({ ...editing, title_en: e.target.value })} placeholder="English title..." className="w-full bg-transparent border-b border-white/20 py-2 mt-1 outline-none focus:border-[#E11D48]" />
                </div>
                <div>
                  <label className="text-xs tracking-wider uppercase text-zinc-400">Excerpt (English)</label>
                  <textarea value={editing.excerpt_en || ""} onChange={(e) => setEditing({ ...editing, excerpt_en: e.target.value })} rows={2} placeholder="English article summary..." className="w-full bg-transparent border border-white/15 p-3 mt-1 outline-none focus:border-[#E11D48] resize-none text-sm" />
                </div>
                <div>
                  <label className="text-xs tracking-wider uppercase text-zinc-400 block mb-2">Content (English)</label>
                  <RichTextEditor value={editing.content_en || ""} onChange={(v) => setEditing({ ...editing, content_en: v })} />
                </div>
              </>
            )}

            {langTab === "fr" && (
              <>
                <div>
                  <label className="text-xs tracking-wider uppercase text-zinc-400">Titre (Français)</label>
                  <input value={editing.title_fr || ""} onChange={(e) => setEditing({ ...editing, title_fr: e.target.value })} placeholder="Titre en français..." className="w-full bg-transparent border-b border-white/20 py-2 mt-1 outline-none focus:border-[#E11D48]" />
                </div>
                <div>
                  <label className="text-xs tracking-wider uppercase text-zinc-400">Résumé (Français)</label>
                  <textarea value={editing.excerpt_fr || ""} onChange={(e) => setEditing({ ...editing, excerpt_fr: e.target.value })} rows={2} placeholder="Résumé de l'article en français..." className="w-full bg-transparent border border-white/15 p-3 mt-1 outline-none focus:border-[#E11D48] resize-none text-sm" />
                </div>
                <div>
                  <label className="text-xs tracking-wider uppercase text-zinc-400 block mb-2">Contenu (Français)</label>
                  <RichTextEditor value={editing.content_fr || ""} onChange={(v) => setEditing({ ...editing, content_fr: v })} />
                </div>
              </>
            )}

            <div className="p-4 bg-zinc-900/60 border border-white/10 rounded-xl space-y-4">
              <h3 className="text-xs tracking-wider uppercase text-[#E11D48] font-bold">Multimedia & Portada</h3>
              <div>
                <label className="text-xs tracking-wider uppercase text-zinc-400 block mb-1">URL de Video (YouTube Embed, Vimeo o MP4) - Portada de Video</label>
                <input
                  type="url"
                  placeholder="https://www.youtube.com/embed/... o https://..."
                  value={editing.video_url || ""}
                  onChange={(e) => setEditing({ ...editing, video_url: e.target.value })}
                  className="w-full bg-black/50 border border-white/15 px-3 py-2 text-sm text-zinc-200 outline-none focus:border-[#E11D48] rounded-md"
                />
                <p className="text-[11px] text-zinc-500 mt-1">Si especificas una URL de video, este artículo aparecerá destacado con reproducción de video interactiva en la revista.</p>
              </div>

              <div>
                <ImageInputUploader
                  label="Imagen de Portada / Miniatura del Artículo"
                  value={editing.cover_url}
                  onChange={(val) => setEditing((p) => ({ ...p, cover_url: val }))}
                  preset="article"
                  aspectRatio="article"
                  helperText="Sube una imagen desde tu equipo o introduce una URL externa. Se optimizará y guardará como WebP ultraligero en nuestro servidor."
                />
              </div>
            </div>

            <div>
              <label className="text-xs tracking-wider uppercase text-zinc-400 block mb-2">Títulos relacionados (del catálogo)</label>
              <div className="relative max-w-md">
                <div className="flex items-center gap-2 border-b border-white/20 focus-within:border-[#E11D48]">
                  <Search size={16} className="text-zinc-400" />
                  <input data-testid="article-movie-search" value={movieQuery} onChange={(e) => setMovieQuery(e.target.value)} placeholder="Buscar película/serie..." className="bg-transparent py-2 text-sm outline-none flex-1" />
                </div>
                {movieResults.length > 0 && (
                  <div className="absolute z-20 mt-1 w-full bg-[#0A0A0C] border border-white/15 max-h-64 overflow-y-auto">
                    {movieResults.map((m) => (
                      <button key={m.id} type="button" data-testid={`article-movie-result-${m.id}`} onClick={() => addMovie(m)} className="flex items-center gap-3 w-full p-2 hover:bg-white/5 text-left">
                        {m.poster_url ? <img src={m.poster_url} alt="" className="w-7 h-10 object-cover" /> : <div className="w-7 h-10 bg-zinc-800" />}
                        <span className="text-sm truncate">{m.title} {m.year && <span className="text-zinc-500">({m.year})</span>}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <div className="flex flex-wrap gap-2 mt-3">
                {relatedMovies.map((m) => (
                  <span key={m.id} className="flex items-center gap-2 border border-white/15 px-3 py-1.5 text-sm" data-testid={`article-related-${m.id}`}>
                    {m.title}
                    <button type="button" onClick={() => removeMovie(m.id)} className="text-zinc-500 hover:text-[#E11D48]"><X size={13} /></button>
                  </span>
                ))}
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-xs tracking-wider uppercase text-zinc-400">Enlaces relacionados</label>
                <button type="button" data-testid="article-add-link" onClick={addLink} className="text-sm text-[#E11D48] flex items-center gap-1"><Plus size={14} /> Añadir</button>
              </div>
              {editing.related_links.map((l, i) => (
                <div key={i} className="flex gap-2 mb-2">
                  <input data-testid={`article-link-label-${i}`} value={l.label} onChange={(e) => setLink(i, "label", e.target.value)} placeholder="Texto" className="w-40 bg-transparent border-b border-white/20 py-1.5 text-sm outline-none focus:border-[#E11D48]" />
                  <input data-testid={`article-link-url-${i}`} value={l.url} onChange={(e) => setLink(i, "url", e.target.value)} placeholder="https://..." className="flex-1 bg-transparent border-b border-white/20 py-1.5 text-sm outline-none focus:border-[#E11D48]" />
                  <button type="button" data-testid={`article-link-remove-${i}`} onClick={() => removeLink(i)} className="text-zinc-500 hover:text-[#E11D48]"><X size={15} /></button>
                </div>
              ))}
            </div>

            <div className="flex flex-wrap items-center gap-3 pt-4 border-t border-white/10">
              <button data-testid="article-save-draft" onClick={() => save(false)} disabled={saving} className="border border-white/20 px-5 py-2.5 hover:bg-white/5 transition-colors disabled:opacity-50">Guardar borrador</button>
              <button data-testid="article-submit-review" onClick={() => save(true)} disabled={saving} className="bg-[#E11D48] text-white px-5 py-2.5 hover:bg-[#BE123C] transition-colors flex items-center gap-2 disabled:opacity-50">
                <Send size={16} /> Enviar a revisión
              </button>
              {isAdmin && editing.id && (
                <button data-testid="article-approve" onClick={() => doAction(editing.id, "approve").then(() => setEditing(null))} className="bg-emerald-600 text-white px-5 py-2.5 hover:bg-emerald-700 transition-colors flex items-center gap-2">
                  <Check size={16} /> Aprobar y publicar
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505]" data-testid="article-editor-list">
      <Navbar />
      <div className="pt-24 px-6 md:px-16 pb-20 max-w-5xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div>
            <Link to="/" className="flex items-center gap-2 text-zinc-400 hover:text-white text-sm mb-2"><ArrowLeft size={15} /> Inicio</Link>
            <h1 className="font-heading text-3xl">{isAdmin ? "Revista · Gestión de artículos" : "Mis artículos"}</h1>
          </div>
          <button data-testid="new-article-button" onClick={openNew} className="bg-[#E11D48] text-white px-5 py-2.5 flex items-center gap-2 hover:bg-[#BE123C] transition-colors">
            <Plus size={16} /> Nuevo artículo
          </button>
        </div>

        {isAdmin && (
          <div className="flex flex-wrap gap-2 mb-6">
            {[["", "Todos"], ["pending", "En revisión"], ["published", "Publicados"], ["draft", "Borradores"], ["rejected", "Rechazados"]].map(([v, l]) => (
              <button key={v || "all"} data-testid={`article-filter-${v || "all"}`} onClick={() => setStatusFilter(v)} className={`px-4 py-2 text-sm border transition-colors ${statusFilter === v ? "border-[#E11D48] text-[#E11D48] bg-[#E11D48]/10" : "border-white/15 text-zinc-400 hover:text-white"}`}>{l}</button>
            ))}
          </div>
        )}

        <div className="border border-white/10 divide-y divide-white/10">
          {articles.map((a) => (
            <div key={a.id} className="flex items-center gap-4 p-4" data-testid={`editor-article-${a.id}`}>
              {a.cover_url ? <img src={a.cover_url} alt="" className="w-16 h-10 object-cover bg-zinc-800" /> : <div className="w-16 h-10 bg-zinc-800" />}
              <div className="flex-1 min-w-0">
                <p className="font-medium truncate">{a.title}</p>
                <p className="text-xs text-zinc-500">{a.category} · <span className={STATUS_COLOR[a.status]}>{STATUS_LABEL[a.status] || a.status}</span>{isAdmin && a.author_name ? ` · ${a.author_name}` : ""}</p>
              </div>
              {a.status === "published" && <Link to={`/revista/${a.slug}`} className="text-xs text-zinc-400 hover:text-white">Ver</Link>}
              <button data-testid={`edit-article-${a.id}`} onClick={() => openEdit(a.id)} className="text-zinc-400 hover:text-white p-2"><Pencil size={16} /></button>
              {isAdmin && a.status === "pending" && (
                <>
                  <button data-testid={`approve-article-${a.id}`} onClick={() => doAction(a.id, "approve")} className="text-emerald-400 hover:text-emerald-300 p-2" title="Aprobar"><Check size={16} /></button>
                  <button data-testid={`reject-article-${a.id}`} onClick={() => doAction(a.id, "reject")} className="text-amber-400 hover:text-amber-300 p-2" title="Rechazar"><XCircle size={16} /></button>
                </>
              )}
              <button data-testid={`delete-article-${a.id}`} onClick={() => doAction(a.id, "delete")} className="text-zinc-400 hover:text-[#E11D48] p-2"><Trash2 size={16} /></button>
            </div>
          ))}
          {articles.length === 0 && <div className="p-6 text-zinc-500 text-sm">Aún no hay artículos. Crea el primero.</div>}
        </div>
      </div>
    </div>
  );
}
